/**
 * Global cookie management
 *
 * Manages the _marketing_ids cookie that stores Google Analytics and Facebook tracking data
 * across all subdomains.
 *
 * @package Felix
 */

/**
 * Get the root domain for cookie sharing
 * @returns {string|null} Root domain with leading dot (e.g., '.websitename.com') or null
 */
const getRootDomain = () => {
	const { hostname } = window.location

	if (hostname.endsWith('.local')) {
		return null
	}

	const parts = hostname.split('.')

	if (parts.length <= 2) {
		return `.${hostname}`
	}

	return `.${parts.slice(-2).join('.')}`
}

/**
 * Get cookie value by name
 * Note: document.cookie only returns cookies accessible to the current domain.
 * Third-party cookies from other domains (e.g., segment.com, google.analytics.com)
 * are not accessible due to same-origin policy, so we only get cookies from our domain.
 * @param {string} name - Cookie name
 * @returns {string|null} Cookie value or null
 */
const getCookie = (name) => {
	const nameEQ = `${name}=`
	const cookie = document.cookie
		.split(';')
		.map((c) => c.trim())
		.find((c) => c.startsWith(nameEQ))

	return cookie ? decodeURIComponent(cookie.substring(nameEQ.length)) : null
}

/**
 * Set cookie with root domain for cross-subdomain sharing
 * @param {string} name - Cookie name
 * @param {string} value - Cookie value
 * @param {number} days - Expiration days (default: 365)
 */
const setCookie = (name, value, days = 365) => {
	const expires = new Date()
	expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000)

	const { hostname } = window.location
	const domain = getRootDomain()

	let cookieString = `${name}=${encodeURIComponent(value)}; expires=${expires.toUTCString()}; path=/`

	const isDevelopment = hostname.endsWith('.local')
	const isWebflowSubdomain = hostname.endsWith('.webflow.io')

	if (domain && !isDevelopment && !isWebflowSubdomain) {
		cookieString += `; domain=${domain}`
	}

	document.cookie = cookieString
}

/**
 * Extract Google Analytics client ID from _ga cookie
 * Returns the full cookie value including GA1 prefix.
 * Only accesses _ga cookie from our root domain (third-party cookies are not accessible).
 * @returns {string|null} Full client ID string (e.g., "GA1.2.123456789.1234567890") or null
 */
const getGAClientId = () => {
	const gaCookie = getCookie('_ga')
	if (!gaCookie) return null

	return gaCookie
}

/**
 * Extract Google click ID from URL parameter
 * @returns {string|null} Click ID or null
 */
const getGoogleClickId = () => {
	return new URLSearchParams(window.location.search).get('gclid')
}

/**
 * Extract Facebook Click ID from _fbc cookie
 * Returns the full cookie value including fb prefix.
 * Only accesses _fbc cookie from our root domain (third-party cookies are not accessible).
 * @returns {string|null} Full click ID string (e.g., "fb.1.1234567890123.9876543210") or null
 */
const getFBClickId = () => {
	const fbcCookie = getCookie('_fbc')
	if (!fbcCookie) return null

	return fbcCookie
}

/**
 * Extract Facebook Click ID from URL parameter (fbclid)
 * @returns {string|null} Click ID or null
 */
const getFacebookClickId = () => {
	return new URLSearchParams(window.location.search).get('fbclid')
}

/**
 * Extract Facebook Pixel ID from _fbp cookie
 * Returns the full cookie value including fb prefix.
 * Only accesses _fbp cookie from our root domain (third-party cookies are not accessible).
 * @returns {string|null} Full pixel ID string (e.g., "fb.1.1234567890123.9876543210") or null
 */
const getFBPixelId = () => {
	const fbpCookie = getCookie('_fbp')
	if (!fbpCookie) return null

	return fbpCookie
}

/**
 * Extract TikTok Click ID from URL parameter (ttclid)
 * TikTok only exposes the click ID via the URL; its _ttp cookie is a
 * browser/pixel identifier, not a click ID, so there is no cookie fallback.
 * @returns {string|null} Click ID or null
 */
const getTiktokClickId = () => {
	return new URLSearchParams(window.location.search).get('ttclid')
}

/**
 * Extract Reddit Click ID from URL parameter (rdt_cid)
 * Reddit only exposes the click ID via the URL; its _rdt_uuid cookie is a
 * pixel UUID, not a click ID, so there is no cookie fallback.
 * @returns {string|null} Click ID or null
 */
const getRedditClickId = () => {
	return new URLSearchParams(window.location.search).get('rdt_cid')
}

/**
 * Extract Pinterest Click ID from URL parameter (epik)
 * @returns {string|null} Click ID or null
 */
const getPinterestClickId = () => {
	return new URLSearchParams(window.location.search).get('epik')
}

/**
 * Extract Pinterest Click ID from _epik cookie
 * The _epik cookie holds the enriched click ID (analogous to Facebook's _fbc),
 * used as a fallback when the epik URL parameter is absent.
 * @returns {string|null} Click ID or null
 */
const getEpikCookie = () => {
	const epikCookie = getCookie('_epik')
	if (!epikCookie) return null

	return epikCookie
}

/**
 * Initialize and update the _marketing_ids cookie
 */
const initStorage = () => {
	try {
		const existingCookie = getCookie('_marketing_ids')
		let storage = {}

		if (existingCookie) {
			try {
				storage = JSON.parse(existingCookie)
			} catch {
				storage = {}
			}
		}

		storage.google ??= {}

		const clientId = getGAClientId()
		if (clientId) {
			storage.google.client_id = clientId
		}

		const clickId = getGoogleClickId()
		if (clickId) {
			storage.google.click_id = clickId
		}

		storage.facebook ??= {}

		const fbClickIdParam = getFacebookClickId()
		const fbClickIdCookie = getFBClickId()

		if (fbClickIdParam) {
			storage.facebook.click_id = fbClickIdParam
		} else if (fbClickIdCookie) {
			storage.facebook.click_id = fbClickIdCookie
		}

		const fbPixelId = getFBPixelId()
		if (fbPixelId) {
			storage.facebook.pixel_id = fbPixelId
		}

		storage.tiktok ??= {}

		const tiktokClickId = getTiktokClickId()
		if (tiktokClickId) {
			storage.tiktok.click_id = tiktokClickId
		}

		storage.reddit ??= {}

		const redditClickId = getRedditClickId()
		if (redditClickId) {
			storage.reddit.click_id = redditClickId
		}

		storage.pinterest ??= {}

		const epikParam = getPinterestClickId()
		const epikCookie = getEpikCookie()

		if (epikParam) {
			storage.pinterest.click_id = epikParam
		} else if (epikCookie) {
			storage.pinterest.click_id = epikCookie
		}

		setCookie('_marketing_ids', JSON.stringify(storage))
	} catch (error) {
		const { hostname } = window.location
		if (hostname.endsWith('.local')) {
			console.error('[_marketing_ids] Error:', error)
		}
	}
}

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', initStorage)
} else {
	initStorage()
}

